/**
 * Identity document expiry helpers and admin filters.
 */

import { getFreelancePermitStatus, photographerIsIndividual } from './freelancePermitService';
import { getDronePermitStatus, photographerUsesDrone } from './dronePermitService';

export const photographerHasIdentityDocument = (profile) =>
  !!profile?.id_document_url
  || (Array.isArray(profile?.identityDocuments) && profile.identityDocuments.length > 0);

export const getIdentityVerificationStatus = (profile) => {
  if (!photographerHasIdentityDocument(profile)) return 'not_uploaded';
  if (profile?.identity_verified === true) return 'approved';
  if (profile?.identity_reviewed_at && profile?.identity_verified === false) return 'rejected';
  if (profile?.verification_status === 'rejected' && !profile?.identity_verified) return 'rejected';
  return 'submitted';
};

export const getIdentityVerificationStatusLabel = (status) => {
  switch (status) {
    case 'approved':
      return 'موثّق';
    case 'submitted':
      return 'قيد المراجعة';
    case 'rejected':
      return 'مرفوض';
    default:
      return 'لم يُرفع بعد';
  }
};

export const getDaysUntilExpiry = (expiresAt) => {
  if (!expiresAt) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return null;
  expiry.setHours(0, 0, 0, 0);
  return Math.ceil((expiry.getTime() - today.getTime()) / 86400000);
};

/**
 * - no_date: admin has not set expiry yet
 * - valid: more than 30 days remaining
 * - expiring_soon: 8–30 days remaining
 * - critical: 1–7 days remaining
 * - expired: past expiry date
 */
export const getIdentityExpiryStatus = (expiresAt) => {
  const daysLeft = getDaysUntilExpiry(expiresAt);
  if (daysLeft === null) return 'no_date';
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= 7) return 'critical';
  if (daysLeft <= 30) return 'expiring_soon';
  return 'valid';
};

export const getIdentityExpiryStatusLabel = (status) => {
  switch (status) {
    case 'valid':
      return 'ساري';
    case 'expiring_soon':
      return 'يقارب على الانتهاء';
    case 'critical':
      return 'ينتهي قريباً';
    case 'expired':
      return 'منتهي';
    default:
      return 'بدون تاريخ انتهاء';
  }
};

export const getIdentityExpiryBadgeClass = (status) => {
  switch (status) {
    case 'valid':
      return 'bg-green-100 text-green-700';
    case 'expiring_soon':
      return 'bg-amber-100 text-amber-800';
    case 'critical':
      return 'bg-orange-100 text-orange-800';
    case 'expired':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

/** True only when identity is verified and not past expiry date. */
export const isIdentityDocumentCurrentlyValid = (profile) => {
  if (!profile?.identity_verified) return false;
  if (!profile.identity_document_expires_at) return true;
  return getIdentityExpiryStatus(profile.identity_document_expires_at) !== 'expired';
};

export const photographerAccountActivationRequirementsMet = (profile) => {
  if (profile?.identity_verified !== true) return false;
  if (!profile?.identity_document_expires_at) return false;
  if (photographerIsIndividual(profile?.entity_type)) {
    if (getFreelancePermitStatus(profile) !== 'approved') return false;
  }
  if (photographerUsesDrone(profile?.shooting_methods)) {
    if (getDronePermitStatus(profile) !== 'approved') return false;
  }
  return true;
};

export const getIdentityActivationBlockReason = (profile) => {
  if (profile?.identity_verified !== true) return 'يجب قبول مستند الهوية أولاً';
  if (!profile?.identity_document_expires_at) return 'يجب إدخال تاريخ انتهاء الهوية وقبولها';
  if (photographerIsIndividual(profile?.entity_type) && getFreelancePermitStatus(profile) !== 'approved') {
    return 'يجب قبول وثيقة العمل الحر أولاً';
  }
  if (photographerUsesDrone(profile?.shooting_methods) && getDronePermitStatus(profile) !== 'approved') {
    return 'يجب قبول تصريح الدرون أولاً';
  }
  return null;
};

export const matchesIdentityFilter = (profile, filterKey) => {
  const status = getIdentityVerificationStatus(profile);

  if (filterKey === 'identity_pending') {
    return photographerHasIdentityDocument(profile) && status === 'submitted';
  }

  if (status !== 'approved') return false;

  const expiryStatus = getIdentityExpiryStatus(profile?.identity_document_expires_at);
  switch (filterKey) {
    case 'identity_expiring_soon':
      return expiryStatus === 'expiring_soon';
    case 'identity_critical':
      return expiryStatus === 'critical';
    case 'identity_expired':
      return expiryStatus === 'expired';
    case 'identity_valid':
      return expiryStatus === 'valid';
    case 'identity_no_expiry':
      return expiryStatus === 'no_date';
    default:
      return false;
  }
};

export const sortByIdentityExpiry = (list, ascending = true) => {
  return [...list].sort((a, b) => {
    const aDate = a.identity_document_expires_at
      ? new Date(a.identity_document_expires_at).getTime()
      : Infinity;
    const bDate = b.identity_document_expires_at
      ? new Date(b.identity_document_expires_at).getTime()
      : Infinity;
    return ascending ? aDate - bDate : bDate - aDate;
  });
};
